import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../types';
import { AdvancedFilterValue } from '../../models/advancedFilters';

export interface FilterState {
  view: AdvancedFilterValue | undefined;
  features: AdvancedFilterValue[];
  includeAllSegmentationLabels: boolean;
  advancedFilters: {
    datasources: AdvancedFilterValue;
    dicomICID: string;
    study: string;
    segmentationICID: string;
    annotationSetName?: string;
    keypointICID: string;
    technician: string;
    created_at_after: string | undefined;
    created_at_before: string | undefined;
    updated_at_after: string | undefined;
    updated_at_before: string | undefined;
  };
  activeTags: AdvancedFilterValue[];
}

const initialState: FilterState = {
  view: undefined,
  features: [],
  includeAllSegmentationLabels: true,
  advancedFilters: {
    datasources: { uuid: '', name: '' },
    dicomICID: '',
    study: '',
    segmentationICID: '',
    annotationSetName: undefined,
    keypointICID: '',
    technician: '',
    created_at_after: undefined,
    created_at_before: undefined,
    updated_at_after: undefined,
    updated_at_before: undefined,
  },
  activeTags: [],
};

export const filterStateSlice = createSlice({
  name: 'filterState',
  initialState,
  reducers: {
    setView: (state, action: PayloadAction<AdvancedFilterValue | undefined>) => {
      state.view = action.payload;
      if (state.view !== action.payload) {
        state.features = [];
      }
    },
    setFeatures: (state, action: PayloadAction<AdvancedFilterValue[]>) => {
      state.features = action.payload;
    },
    setIncludeAllSegmentationLabels: (state, action: PayloadAction<boolean>) => {
      state.includeAllSegmentationLabels = action.payload;
    },
    setAdvancedFilter: (
      state,
      action: PayloadAction<{
        key: string;
        value: AdvancedFilterValue | AdvancedFilterValue[] | string | string[] | undefined;
      }>
    ) => {
      const { key, value } = action.payload;
      state.advancedFilters = {
        ...state.advancedFilters,
        [key]: value,
      };
    },
    resetFilters: state => {
      state.view = initialState.view;
      state.features = initialState.features;
      state.includeAllSegmentationLabels = initialState.includeAllSegmentationLabels;
      state.advancedFilters = initialState.advancedFilters;
      state.activeTags = initialState.activeTags;
    },
    updateActiveTags: (state, action: PayloadAction<AdvancedFilterValue[]>) => {
      state.activeTags = action.payload;
    },
    removeFilterTag: (state, action: PayloadAction<AdvancedFilterValue>) => {
      const tag = action.payload;

      state.features = state.features.filter(feature => feature.uuid !== tag.uuid);

      if (state.view?.uuid === tag.uuid) {
        state.view = undefined;
      }

      Object.keys(state.advancedFilters).forEach(key => {
        const filterKey = key as keyof typeof state.advancedFilters;
        const filterValue = state.advancedFilters[filterKey];

        if (Array.isArray(filterValue)) {
          // support arrays of objects ({uuid,..}) and primitive string arrays
          const isStringArray = filterValue.length === 0 || typeof filterValue[0] === 'string';
          if (isStringArray) {
            (state.advancedFilters as any)[filterKey] = filterValue.filter((item: any) => {
              // items are primitive strings; tags for string items are stored as `${key}_${item}`
              const itemUuid = `${filterKey}_${item}`;
              return itemUuid !== tag.uuid && item !== tag.uuid;
            }) as any;
          } else {
            state.advancedFilters[filterKey] = filterValue.filter(
              item => item.uuid !== tag.uuid
            ) as any;
          }
        } else if (typeof filterValue === 'object' && filterValue !== null) {
          if (filterValue.uuid === tag.uuid) {
            state.advancedFilters[filterKey] = { uuid: '', name: '' } as any;
          }
        } else if (typeof filterValue === 'string') {
          if (tag.name.includes(filterValue) && filterValue !== '') {
            state.advancedFilters[filterKey] = '' as any;
          }
        }
      });

      state.activeTags = state.activeTags.filter(t => t.uuid !== tag.uuid);
    },
  },
});

export const {
  setView,
  setFeatures,
  setIncludeAllSegmentationLabels,
  setAdvancedFilter,
  resetFilters,
  updateActiveTags,
  removeFilterTag,
} = filterStateSlice.actions;

export const selectFilterState = (state: RootState) => state.filterState;
export const selectActiveFilters = (state: RootState) => state.filterState.activeTags;
export default filterStateSlice.reducer;
